import React, { useState, useMemo, useEffect } from 'react';
import {
    WeeklyPlan, Subcontractor, Contract, Task, ProtocolState,
    SubcontractorEvaluationConfig, EvaluationParameter, GlobalUser, Responsible
} from '../../types';
import {
    EvaluationRangeBadge, DEFAULT_EVALUATION_RANGES
} from './EvaluationConfigModal';

interface SubcontractorEvaluationModalProps {
    isOpen: boolean;
    onClose: () => void;
    weeklyPlans: WeeklyPlan[];
    subcontractors: Subcontractor[];
    contracts?: Contract[];
    tasks?: Task[];
    savedProtocols?: Record<string, ProtocolState>;
    lookaheadPlans?: any[];
    evaluationConfig: SubcontractorEvaluationConfig;
    onOpenConfigModal?: () => void;
    hasEditPermission?: boolean;
    projectId?: string;
    initialPlanId?: number | null;
    globalUsers?: GlobalUser[];
    responsibles?: Responsible[];
}

export const SubcontractorEvaluationModal: React.FC<SubcontractorEvaluationModalProps> = ({
    isOpen,
    onClose,
    weeklyPlans = [],
    subcontractors = [],
    contracts = [],
    tasks = [],
    savedProtocols = {},
    lookaheadPlans = [],
    evaluationConfig,
    onOpenConfigModal,
    hasEditPermission = true,
    projectId = 'default',
    initialPlanId = null,
    globalUsers = [],
    responsibles = []
}) => {
    // Current selected week for evaluation (null = weeks list view, number = matrix view)
    const [selectedPlanId, setSelectedPlanId] = useState<number | null>(() => initialPlanId || null);

    useEffect(() => {
        if (isOpen && initialPlanId !== undefined) {
            setSelectedPlanId(initialPlanId);
        }
    }, [isOpen, initialPlanId]);

    // Active tooltip/modal for parameter indicator description
    const [infoModalParam, setInfoModalParam] = useState<EvaluationParameter | null>(null);

    // Filter query
    const [weekSearch, setWeekSearch] = useState('');
    const [subSearch, setSubSearch] = useState('');

    // Storage key for weekly evaluations
    const storageKey = `klydapp_sc_evaluations_${projectId}`;

    // Evaluations State: Record<weekId, Record<subcontractorId, Record<parameterId, number>>>
    const [evaluations, setEvaluations] = useState<Record<string, Record<string, Record<string, number>>>>(() => {
        try {
            const saved = localStorage.getItem(storageKey);
            if (saved) return JSON.parse(saved);
        } catch (e) {
            console.error('Error loading evaluations:', e);
        }
        return {};
    });

    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
    const [saveNotification, setSaveNotification] = useState<string | null>(null);

    // Active Subcontractors list
    const activeSubcontractors = useMemo(() => {
        return subcontractors.filter(s => s.status !== 'Inactive');
    }, [subcontractors]);

    // Active areas & ranges from config
    const areas = useMemo(() => evaluationConfig?.areas || [], [evaluationConfig]);
    const ranges = useMemo(() => evaluationConfig?.ranges || DEFAULT_EVALUATION_RANGES, [evaluationConfig]);

    // Currently selected weekly plan
    const currentPlan = useMemo(() => {
        if (selectedPlanId === null) return null;
        return weeklyPlans.find(p => p.id === selectedPlanId) || null;
    }, [weeklyPlans, selectedPlanId]);

    // Helper: Identify all users belonging to this subcontractor
    const getSubcontractorUsers = (sub: Subcontractor) => {
        const subRazonNorm = (sub.razonSocial || '').trim().toLowerCase();
        const subRutNorm = (sub.rut || '').trim().toLowerCase().replace(/[^0-9k]/g, '');
        const linkedSet = new Set(sub.linkedUserIds || []);

        const matchedMap = new Map<string, { id: string; name: string; email?: string }>();

        // 1. Search in globalUsers
        (globalUsers || []).forEach(u => {
            if (!u || u.status === 'Inactive') return;
            const uComp = (u.companyName || '').trim().toLowerCase();
            const matchesComp = Boolean(uComp && subRazonNorm && (uComp === subRazonNorm || uComp.includes(subRazonNorm) || subRazonNorm.includes(uComp)));
            const matchesLinked = linkedSet.has(u.id);

            if (matchesComp || matchesLinked) {
                matchedMap.set(u.id, { id: u.id, name: u.name, email: u.email });
            }
        });

        // 2. Search in project responsibles (fallback/supplement)
        (responsibles || []).forEach(r => {
            if (!r) return;
            const rComp = (r.companyName || '').trim().toLowerCase();
            const matchesComp = Boolean(rComp && subRazonNorm && (rComp === subRazonNorm || rComp.includes(subRazonNorm) || subRazonNorm.includes(rComp)));
            const matchesLinked = linkedSet.has(r.id);
            const matchesSubId = r.id === sub.id;

            if (matchesComp || matchesLinked || matchesSubId) {
                if (!matchedMap.has(r.id)) {
                    matchedMap.set(r.id, { id: r.id, name: r.name, email: r.email });
                }
            }
        });

        return Array.from(matchedMap.values());
    };

    // Auto-calculate values for automatic parameters averaging active assigned users
    const calculateAutoMetric = (param: EvaluationParameter, sub: Subcontractor, plan: WeeklyPlan): number | null => {
        const assignedUsers = getSubcontractorUsers(sub);
        const assignedUserIds = new Set(assignedUsers.map(u => u.id));
        const assignedUserNames = new Set(assignedUsers.map(u => u.name.trim().toLowerCase()));

        // Contract task mappings for fallback context
        const subContract = contracts.find(c => c.subcontractorId === sub.id);
        const subTaskIds = new Set<string>();
        if (subContract?.items) {
            subContract.items.forEach(it => { if (it.id) subTaskIds.add(it.id); });
        }
        tasks.forEach(t => {
            if (t.responsibleId === sub.id || assignedUserIds.has(t.responsibleId) || (subContract?.items && subContract.items.some(it => it.id === t.id))) {
                subTaskIds.add(t.id);
            }
        });

        // -------------------------------------------------------------
        // 1. PPC (Porcentaje de Plan Cumplido)
        // PPC_Subcontrato = (Suma de PPC de usuarios activos) / (Total de usuarios activos)
        // -------------------------------------------------------------
        if (param.automaticCode === 'PPC') {
            const userPpcValues: number[] = [];

            assignedUsers.forEach(u => {
                // Filter allocations belonging to this user in the week
                const userAllocations = (plan.allocations || []).filter(a =>
                    a.responsibleId === u.id ||
                    (subTaskIds.has(a.taskId) && a.responsibleId === sub.id)
                );

                // User is active if they have allocations in this week
                if (userAllocations.length > 0) {
                    const validAllocations = userAllocations.filter(a => a.executed !== null);
                    const listToCount = validAllocations.length > 0 ? validAllocations : userAllocations;
                    const executed = listToCount.filter(a => a.executed === true).length;
                    const userPpc = (executed / listToCount.length) * 100;
                    userPpcValues.push(userPpc);
                }
            });

            // If users were found with allocations, return arithmetic average
            if (userPpcValues.length > 0) {
                const sum = userPpcValues.reduce((acc, curr) => acc + curr, 0);
                return Math.round((sum / userPpcValues.length) * 10) / 10;
            }

            // Fallback: check direct allocations mapped to sub.id or subcontractor's tasks
            const subAllocations = (plan.allocations || []).filter(a => 
                a.responsibleId === sub.id || (subTaskIds.size > 0 && subTaskIds.has(a.taskId))
            );
            if (subAllocations.length > 0) {
                const validAllocations = subAllocations.filter(a => a.executed !== null);
                const listToCount = validAllocations.length > 0 ? validAllocations : subAllocations;
                const executed = listToCount.filter(a => a.executed === true).length;
                return Math.round(((executed / listToCount.length) * 100) * 10) / 10;
            }

            // No active users or commitments in period -> N/A
            return null;
        }

        // -------------------------------------------------------------
        // 2. PCR (Porcentaje de Restricciones Liberadas)
        // PCR_Subcontrato = (Suma de PCR de usuarios activos en la semana) / (Total de usuarios activos)
        // Restricciones cuya fecha requerida pertenezca a la semana evaluada [plan.startDate, plan.endDate]
        // -------------------------------------------------------------
        if (param.automaticCode === 'PCR') {
            const userPcrValues: number[] = [];

            // Filtrar restricciones que aplican a la semana evaluada
            const weekStartDate = (plan.startDate || '').substring(0, 10);
            const weekEndDate = (plan.endDate || '').substring(0, 10);
            const planIdStr = String(plan.id);

            const allConstraints: any[] = [];
            (lookaheadPlans || []).forEach(lp => {
                (lp.constraints || []).forEach((c: any) => {
                    const reqDate = (c.requiredDate || '').substring(0, 10);
                    const isInWeek = (reqDate && weekStartDate && weekEndDate && reqDate >= weekStartDate && reqDate <= weekEndDate) ||
                                     (c.originPlanId && String(c.originPlanId) === planIdStr);
                    if (isInWeek) {
                        allConstraints.push(c);
                    }
                });
            });

            assignedUsers.forEach(u => {
                const userConstraints = allConstraints.filter(c =>
                    c.responsibleId === u.id || (c.responsible && c.responsible.trim().toLowerCase() === u.name.trim().toLowerCase())
                );

                if (userConstraints.length > 0) {
                    const liberated = userConstraints.filter(c => {
                        const s = (c.status || '').toLowerCase();
                        return s === 'liberado' || s === 'liberada' || s.includes('liber');
                    }).length;
                    const userPcr = (liberated / userConstraints.length) * 100;
                    userPcrValues.push(userPcr);
                }
            });

            if (userPcrValues.length > 0) {
                const sum = userPcrValues.reduce((acc, curr) => acc + curr, 0);
                return Math.round((sum / userPcrValues.length) * 10) / 10;
            }

            // Fallback: check constraints assigned directly to sub.id or sub.razonSocial in this week
            const subConstraints = allConstraints.filter(c =>
                c.responsibleId === sub.id || 
                (c.responsible && (c.responsible === sub.razonSocial || c.responsible.trim().toLowerCase() === sub.razonSocial.trim().toLowerCase()))
            );
            if (subConstraints.length > 0) {
                const liberated = subConstraints.filter(c => {
                    const s = (c.status || '').toLowerCase();
                    return s === 'liberado' || s === 'liberada' || s.includes('liber');
                }).length;
                return Math.round(((liberated / subConstraints.length) * 100) * 10) / 10;
            }

            // No active users with constraints in period -> N/A
            return null;
        }

        // -------------------------------------------------------------
        // 3. CONFIABILIDAD DE CALIDAD
        // Confiabilidad_Subcontrato = (Suma de Confiabilidad de usuarios activos en la semana) / (Total de usuarios activos)
        // Protocolos con actividad (inspección / aprobación / resolución) en el rango [plan.startDate, plan.endDate]
        // -------------------------------------------------------------
        if (param.automaticCode === 'CONFIABILIDAD') {
            const userConfValues: number[] = [];
            const weekStartDate = (plan.startDate || '').substring(0, 10);
            const weekEndDate = (plan.endDate || '').substring(0, 10);

            // Filtrar protocolos activos que tuvieron eventos o resolución dentro de la semana evaluada
            const allProtocols = Object.values(savedProtocols || {}).filter(sp => {
                if (!sp || sp.isDeleted) return false;
                // Si tiene historial, verificar si hubo acciones en la semana
                if (sp.history && Array.isArray(sp.history) && sp.history.length > 0) {
                    return sp.history.some(h => {
                        const hDate = (h.date || '').substring(0, 10);
                        return (!weekStartDate || hDate >= weekStartDate) && (!weekEndDate || hDate <= weekEndDate);
                    });
                }
                // Si la tarea asociada tuvo asignaciones en este plan semanal
                if (sp.taskId && subTaskIds.has(sp.taskId)) {
                    return (plan.allocations || []).some(a => a.taskId === sp.taskId);
                }
                return false;
            });

            assignedUsers.forEach(u => {
                const uNameNorm = u.name.trim().toLowerCase();
                // Find protocols linked to this user (via task responsible or protocol reviewer history)
                const userProtocols = allProtocols.filter(sp => {
                    if (sp.taskId && subTaskIds.has(sp.taskId)) return true;
                    if (sp.history && sp.history.some(h => (h.user || '').trim().toLowerCase() === uNameNorm)) return true;
                    return false;
                });

                if (userProtocols.length > 0) {
                    const approved = userProtocols.filter(sp => {
                        const s = (sp.status || '').toLowerCase();
                        return s === 'cerrado' || s === 'resuelto' || s === 'aprobado';
                    }).length;
                    const userConf = (approved / userProtocols.length) * 100;
                    userConfValues.push(userConf);
                }
            });

            if (userConfValues.length > 0) {
                const sum = userConfValues.reduce((acc, curr) => acc + curr, 0);
                return Math.round((sum / userConfValues.length) * 10) / 10;
            }

            // Fallback: check protocols for tasks belonging to this subcontractor in this week
            const subProtocols = allProtocols.filter(sp => sp.taskId && subTaskIds.has(sp.taskId));
            if (subProtocols.length > 0) {
                const approved = subProtocols.filter(sp => {
                    const s = (sp.status || '').toLowerCase();
                    return s === 'cerrado' || s === 'resuelto' || s === 'aprobado';
                }).length;
                return Math.round(((approved / subProtocols.length) * 100) * 10) / 10;
            }

            // No active quality protocols for this subcontrato -> N/A
            return null;
        }

        // -------------------------------------------------------------
        // 4. CNC (Causas de No Cumplimiento)
        // -------------------------------------------------------------
        if (param.automaticCode === 'CNC') {
            const subAllocations = (plan.allocations || []).filter(a =>
                assignedUserIds.has(a.responsibleId) || a.responsibleId === sub.id || (subTaskIds.size > 0 && subTaskIds.has(a.taskId))
            );

            if (subAllocations.length === 0) return null;

            const cncCount = subAllocations.filter(a => a.cncId || a.executed === false).length;
            if (cncCount === 0) return 100;
            if (cncCount <= 5) return 70;
            if (cncCount <= 10) return 50;
            return 10;
        }

        return 100;
    };

    // Get score for a specific cell (returns number or null for N/A)
    const getCellScore = (planId: number, subId: string, param: EvaluationParameter, plan: WeeklyPlan): number | null => {
        // Strict business rule: PPC, PCR, and CONFIABILIDAD cannot be manual inputs.
        // If the parameter is Automatico, always compute from current period data.
        if (param.indicatorType === 'Automatico') {
            const targetSub = subcontractors.find(s => s.id === subId) || { id: subId, razonSocial: '', rut: '', giro: '', representante: '', telefono: '', correo: '' };
            return calculateAutoMetric(param, targetSub, plan);
        }

        const weekData = evaluations[String(planId)];
        const subData = weekData?.[subId];
        if (subData && subData[param.id] !== undefined) {
            return subData[param.id];
        }

        return 100; // Default score for manual parameters
    };

    // Update cell score (only permitted for manual parameters)
    const handleScoreChange = (planId: number, subId: string, paramId: string, value: number) => {
        const clamped = Math.max(0, Math.min(100, isNaN(value) ? 0 : value));
        setEvaluations(prev => {
            const planKey = String(planId);
            const currentPlanData = prev[planKey] || {};
            const currentSubData = currentPlanData[subId] || {};

            return {
                ...prev,
                [planKey]: {
                    ...currentPlanData,
                    [subId]: {
                        ...currentSubData,
                        [paramId]: clamped
                    }
                }
            };
        });
        setHasUnsavedChanges(true);
    };

    // Save evaluations
    const handleSaveEvaluations = () => {
        try {
            localStorage.setItem(storageKey, JSON.stringify(evaluations));
            setHasUnsavedChanges(false);
            setSaveNotification('Evaluación guardada exitosamente.');
            setTimeout(() => setSaveNotification(null), 3000);
        } catch (e) {
            console.error('Error saving evaluations:', e);
            alert('Error al guardar la evaluación.');
        }
    };

    // Calculate final weighted score for a subcontractor in a weekly plan
    const calculateSubcontractorTotalScore = (planId: number, subId: string, plan: WeeklyPlan): number | null => {
        if (!areas || areas.length === 0) return 100;

        let totalScore = 0;
        let totalEffectiveAreaWeights = 0;
        let hasAnyValidParam = false;

        areas.forEach(area => {
            const areaWeight = Number(area.weight) || 0;
            if (areaWeight <= 0) return;

            let areaScoreSum = 0;
            let areaValidParamWeights = 0;

            if (area.parameters && area.parameters.length > 0) {
                area.parameters.forEach(param => {
                    const score = getCellScore(planId, subId, param, plan);
                    const paramWeight = Number(param.weight) || 0;
                    if (score !== null && !isNaN(score) && paramWeight > 0) {
                        areaScoreSum += score * paramWeight;
                        areaValidParamWeights += paramWeight;
                        hasAnyValidParam = true;
                    }
                });
            }

            if (areaValidParamWeights > 0) {
                const areaAverage = areaScoreSum / areaValidParamWeights;
                totalScore += areaAverage * areaWeight;
                totalEffectiveAreaWeights += areaWeight;
            }
        });

        if (!hasAnyValidParam || totalEffectiveAreaWeights === 0) {
            return null; // All parameters are N/A
        }

        const finalScore = totalScore / totalEffectiveAreaWeights;
        return Math.round(finalScore * 10) / 10;
    };

    // Calculate week average across all active subcontractors
    const calculateWeekAverage = (planId: number, plan: WeeklyPlan): number => {
        if (activeSubcontractors.length === 0) return 0;
        let sum = 0;
        let count = 0;
        activeSubcontractors.forEach(sub => {
            const score = calculateSubcontractorTotalScore(planId, sub.id, plan);
            if (score !== null) {
                sum += score;
                count++;
            }
        });
        return count > 0 ? Math.round((sum / count) * 10) / 10 : 0;
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-3 md:p-4 bg-slate-900/80 backdrop-blur-sm animate-in fade-in duration-200">
            <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-7xl h-[92vh] flex flex-col overflow-hidden">
                
                {/* Header */}
                <div className="px-5 py-3.5 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50/90 dark:bg-slate-800/80 shrink-0 gap-3">
                    <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-xl bg-blue-600 text-white flex items-center justify-center shrink-0 shadow-sm">
                            <span className="material-symbols-outlined text-[24px]">rate_review</span>
                        </div>
                        <div>
                            <div className="flex items-center gap-2">
                                <h3 className="text-sm md:text-base font-black text-slate-800 dark:text-white leading-tight">
                                    Evaluación de Subcontratos (SC)
                                </h3>
                                {selectedPlanId !== null && (
                                    <span className="px-2 py-0.5 rounded-md bg-blue-100 dark:bg-blue-950/60 text-blue-700 dark:text-blue-300 text-xs font-bold border border-blue-200 dark:border-blue-900/40">
                                        Semana #{selectedPlanId}
                                    </span>
                                )}
                            </div>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                                {selectedPlanId === null 
                                    ? 'Listado de semanas del proyecto para la evaluación periódica de subcontratistas.'
                                    : `Matriz de evaluación para la semana del ${currentPlan?.startDate} al ${currentPlan?.endDate}.`
                                }
                            </p>
                        </div>
                    </div>

                    <div className="flex items-center gap-2">
                        {saveNotification && (
                            <span className="text-xs font-bold text-emerald-600 dark:text-emerald-400 flex items-center gap-1 bg-emerald-50 dark:bg-emerald-950/30 px-3 py-1 rounded-lg border border-emerald-200 dark:border-emerald-800 animate-in fade-in duration-200">
                                <span className="material-symbols-outlined text-[16px]">check_circle</span>
                                {saveNotification}
                            </span>
                        )}

                        {selectedPlanId !== null && hasEditPermission && (
                            <button
                                onClick={handleSaveEvaluations}
                                className={`flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 active:scale-95 text-white text-xs font-black uppercase tracking-wider rounded-xl shadow-md transition-all cursor-pointer ${
                                    hasUnsavedChanges ? 'ring-2 ring-emerald-400 ring-offset-2' : ''
                                }`}
                            >
                                <span className="material-symbols-outlined text-[18px]">save</span>
                                Guardar Evaluación
                            </button>
                        )}

                        {onOpenConfigModal && (
                            <button
                                onClick={onOpenConfigModal}
                                className="flex items-center gap-1.5 px-3 py-2 bg-white dark:bg-slate-800 border border-slate-300 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-700/50 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl shadow-xs transition-all cursor-pointer"
                                title="Configurar áreas, parámetros y rangos"
                            >
                                <span className="material-symbols-outlined text-[18px] text-blue-600 dark:text-blue-400">tune</span>
                                <span className="hidden sm:inline">Configuración</span>
                            </button>
                        )}

                        <button
                            onClick={onClose}
                            className="p-2 rounded-xl text-slate-400 hover:text-slate-600 dark:hover:text-white hover:bg-slate-200 dark:hover:bg-slate-700 transition-colors cursor-pointer"
                            title="Cerrar ventana"
                        >
                            <span className="material-symbols-outlined text-[20px]">close</span>
                        </button>
                    </div>
                </div>

                {/* Body Content */}
                <div className="flex-1 overflow-y-auto p-4 md:p-6 bg-slate-50 dark:bg-slate-950">
                    
                    {/* ========================================================= */}
                    {/* VIEW 1: LISTADO DE SEMANAS DEL PROYECTO (3 COLUMNAS)      */}
                    {/* ========================================================= */}
                    {selectedPlanId === null ? (
                        <div className="space-y-4">
                            {/* Summary / Search Banner */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                                <div>
                                    <h4 className="text-sm font-bold text-slate-800 dark:text-white">
                                        Semanas de Planificación Registradas
                                    </h4>
                                    <p className="text-xs text-slate-500">
                                        Selecciona una semana para abrir su matriz y evaluar a los subcontratistas.
                                    </p>
                                </div>
                                <div className="relative w-full sm:w-64">
                                    <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-[18px]">search</span>
                                    <input
                                        type="text"
                                        placeholder="Buscar por fecha o número..."
                                        value={weekSearch}
                                        onChange={e => setWeekSearch(e.target.value)}
                                        className="w-full pl-9 pr-3 py-1.5 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl text-xs font-semibold text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                            </div>

                            {/* Weeks Table (3 Columns: Semana #, Fecha Inicio / Fecha Término, Acción Ver) */}
                            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                                {weeklyPlans.length === 0 ? (
                                    <div className="flex flex-col items-center justify-center p-12 text-center text-slate-400">
                                        <span className="material-symbols-outlined text-5xl mb-2 text-slate-300 dark:text-slate-600">calendar_month</span>
                                        <p className="text-sm font-bold text-slate-700 dark:text-slate-300">No hay planificaciones semanales registradas</p>
                                        <p className="text-xs text-slate-500 max-w-sm mt-1">
                                            Crea planificaciones en el módulo de Planificación Semanal para que aparezcan aquí automáticamente.
                                        </p>
                                    </div>
                                ) : (
                                    <table className="w-full text-left border-collapse">
                                        <thead>
                                            <tr className="bg-slate-50 dark:bg-slate-800/60 border-b border-slate-200 dark:border-slate-800 text-[11px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">
                                                <th className="px-6 py-4 w-48">Semana #</th>
                                                <th className="px-6 py-4">Fecha Inicio / Fecha Término</th>
                                                <th className="px-6 py-4 w-40 text-center">Evaluación General</th>
                                                <th className="px-6 py-4 w-36 text-right">Acción</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800 font-medium">
                                            {[...weeklyPlans]
                                                .filter(plan => {
                                                    const q = weekSearch.toLowerCase();
                                                    return (
                                                        String(plan.id).includes(q) ||
                                                        (plan.startDate && plan.startDate.includes(q)) ||
                                                        (plan.endDate && plan.endDate.includes(q)) ||
                                                        (plan.status && plan.status.toLowerCase().includes(q))
                                                    );
                                                })
                                                .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime())
                                                .map(plan => {
                                                    const isEvaluated = evaluations[String(plan.id)] !== undefined;
                                                    const weekAvg = calculateWeekAverage(plan.id, plan);

                                                    return (
                                                        <tr 
                                                            key={plan.id}
                                                            className="hover:bg-slate-50/70 dark:hover:bg-slate-800/40 transition-colors cursor-pointer group"
                                                            onClick={() => setSelectedPlanId(plan.id)}
                                                        >
                                                            {/* 1. Semana # */}
                                                            <td className="px-6 py-4">
                                                                <div className="flex items-center gap-2.5">
                                                                    <span className="size-8 rounded-xl bg-blue-50 dark:bg-blue-950/50 text-blue-600 dark:text-blue-400 font-black text-xs flex items-center justify-center border border-blue-200 dark:border-blue-900/40 shadow-xs">
                                                                        #{plan.id}
                                                                    </span>
                                                                    <div className="flex flex-col">
                                                                        <span className="font-bold text-sm text-slate-800 dark:text-white">
                                                                            Semana #{plan.id}
                                                                        </span>
                                                                        <span className="text-[10px] text-slate-400 font-bold uppercase">
                                                                            {plan.status || 'Planificada'}
                                                                        </span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* 2. Fecha Inicio / Fecha Término */}
                                                            <td className="px-6 py-4">
                                                                <div className="flex flex-col">
                                                                    <div className="text-xs font-bold text-slate-800 dark:text-slate-200 flex items-center gap-1.5">
                                                                        <span className="text-[10px] text-slate-400 font-bold uppercase">Inicio:</span>
                                                                        <span>{plan.startDate}</span>
                                                                    </div>
                                                                    <div className="text-[11px] text-slate-500 dark:text-slate-400 font-medium flex items-center gap-1.5 mt-0.5">
                                                                        <span className="text-[10px] text-slate-400 font-bold uppercase">Término:</span>
                                                                        <span>{plan.endDate}</span>
                                                                    </div>
                                                                </div>
                                                            </td>

                                                            {/* Evaluación General (Badge) */}
                                                            <td className="px-6 py-4 text-center">
                                                                {isEvaluated ? (
                                                                    <EvaluationRangeBadge
                                                                        score={weekAvg}
                                                                        ranges={ranges}
                                                                        showDistinction={true}
                                                                        showScore={true}
                                                                    />
                                                                ) : (
                                                                    <span className="text-[11px] text-slate-400 font-bold italic bg-slate-100 dark:bg-slate-800 px-2.5 py-1 rounded-full">
                                                                        Pendiente
                                                                    </span>
                                                                )}
                                                            </td>

                                                            {/* 3. Acción (Botón Ver) */}
                                                            <td className="px-6 py-4 text-right">
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setSelectedPlanId(plan.id);
                                                                    }}
                                                                    className="px-4 py-2 bg-blue-600 group-hover:bg-blue-700 active:scale-95 text-white text-xs font-bold rounded-xl shadow-xs transition-all flex items-center gap-1.5 ml-auto cursor-pointer"
                                                                >
                                                                    <span className="material-symbols-outlined text-[16px]">visibility</span>
                                                                    Ver
                                                                </button>
                                                            </td>
                                                        </tr>
                                                    );
                                                })}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                        </div>
                    ) : (
                        // =========================================================
                        // VIEW 2: MATRIZ DE EVALUACIÓN SEMANAL INTERACTIVA
                        // =========================================================
                        <div className="space-y-4">
                            {/* Breadcrumb & Navigation */}
                            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-slate-900 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs">
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={() => setSelectedPlanId(null)}
                                        className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 text-xs font-bold rounded-xl transition-all cursor-pointer"
                                    >
                                        <span className="material-symbols-outlined text-[16px]">arrow_back</span>
                                        Volver a Semanas
                                    </button>
                                    <div>
                                        <h4 className="text-sm font-black text-slate-800 dark:text-white flex items-center gap-2">
                                            <span>Semana #{selectedPlanId}</span>
                                            <span className="text-xs text-slate-500 font-normal">
                                                ({currentPlan?.startDate} al {currentPlan?.endDate})
                                            </span>
                                        </h4>
                                    </div>
                                </div>

                                <div className="flex items-center gap-2">
                                    <div className="relative w-full sm:w-48">
                                        <span className="material-symbols-outlined absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400 text-[16px]">search</span>
                                        <input
                                            type="text"
                                            placeholder="Filtrar subcontrato..."
                                            value={subSearch}
                                            onChange={e => setSubSearch(e.target.value)}
                                            className="w-full pl-8 pr-3 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-xs font-medium text-slate-800 dark:text-white outline-none focus:ring-1 focus:ring-blue-500"
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Main Matrix Table */}
                            {activeSubcontractors.length === 0 ? (
                                <div className="p-12 text-center text-slate-400 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                                    <span className="material-symbols-outlined text-5xl mb-2 text-slate-300">domain_disabled</span>
                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">No hay Subcontratistas activos registrados</p>
                                    <p className="text-xs text-slate-500 mt-1">Registra empresas contratistas en el módulo de Subcontratos.</p>
                                </div>
                            ) : areas.length === 0 ? (
                                <div className="p-12 text-center text-slate-400 bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800">
                                    <span className="material-symbols-outlined text-5xl mb-2 text-slate-300">settings_suggest</span>
                                    <p className="text-sm font-bold text-slate-700 dark:text-slate-300">No hay áreas de evaluación configuradas</p>
                                    <p className="text-xs text-slate-500 mt-1">Haz clic en Configuración para definir áreas y parámetros.</p>
                                </div>
                            ) : (
                                <div className="bg-white dark:bg-slate-900 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-xs overflow-hidden">
                                    <div className="overflow-x-auto max-h-[65vh]">
                                        <table className="w-full text-left border-collapse">
                                            {/* Table Header */}
                                            <thead className="sticky top-0 z-20 bg-slate-100 dark:bg-slate-800 border-b border-slate-200 dark:border-slate-700 text-xs">
                                                <tr>
                                                    {/* Fixed Columns */}
                                                    <th className="px-4 py-3 min-w-[150px] w-48 font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider bg-slate-100 dark:bg-slate-800 sticky left-0 z-30 border-r border-slate-200 dark:border-slate-700">
                                                        Área
                                                    </th>
                                                    <th className="px-4 py-3 min-w-[240px] font-black text-slate-600 dark:text-slate-300 uppercase tracking-wider bg-slate-100 dark:bg-slate-800 sticky left-48 z-30 border-r border-slate-200 dark:border-slate-700">
                                                        Parámetro de Evaluación
                                                    </th>

                                                    {/* Subcontractors Columns (Fixed Width) */}
                                                    {activeSubcontractors
                                                        .filter(s => s.razonSocial.toLowerCase().includes(subSearch.toLowerCase()) || s.rut.toLowerCase().includes(subSearch.toLowerCase()))
                                                        .map(sub => (
                                                            <th 
                                                                key={sub.id} 
                                                                className="px-4 py-3 min-w-[200px] w-56 text-center font-bold text-slate-700 dark:text-slate-200 border-r border-slate-200 dark:border-slate-700"
                                                            >
                                                                <div className="flex flex-col items-center">
                                                                    <span className="font-extrabold text-xs text-slate-800 dark:text-white truncate max-w-[180px]" title={sub.razonSocial}>
                                                                        {sub.razonSocial}
                                                                    </span>
                                                                    <span className="text-[10px] text-slate-400 font-medium">
                                                                        RUT: {sub.rut || 'S/RUT'}
                                                                    </span>
                                                                </div>
                                                            </th>
                                                        ))}
                                                </tr>
                                            </thead>

                                            {/* Table Body */}
                                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 text-xs font-medium">
                                                {areas.map((area) => (
                                                    <React.Fragment key={area.id}>
                                                        {area.parameters.map((param, paramIdx) => (
                                                            <tr 
                                                                key={param.id}
                                                                className="hover:bg-slate-50/70 dark:hover:bg-slate-800/30 transition-colors"
                                                            >
                                                                {/* Col 1: Área (RowSpan on first param) */}
                                                                {paramIdx === 0 && (
                                                                    <td 
                                                                        rowSpan={area.parameters.length}
                                                                        className="px-4 py-3 bg-slate-50/90 dark:bg-slate-850 sticky left-0 z-10 border-r border-slate-200 dark:border-slate-800 align-top"
                                                                    >
                                                                        <div className="flex flex-col gap-1">
                                                                            <span className="font-black text-xs text-slate-800 dark:text-white">
                                                                                {area.name}
                                                                            </span>
                                                                            <span className="text-[10px] font-bold text-blue-600 dark:text-blue-400">
                                                                                Pond: {area.weight}%
                                                                            </span>
                                                                        </div>
                                                                    </td>
                                                                )}

                                                                {/* Col 2: Parámetro + Icono de Información */}
                                                                <td className="px-4 py-2.5 bg-white dark:bg-slate-900 sticky left-48 z-10 border-r border-slate-200 dark:border-slate-800">
                                                                    <div className="flex items-center justify-between gap-2">
                                                                        <div className="flex flex-col">
                                                                            <span className="font-bold text-slate-800 dark:text-slate-200">
                                                                                {param.name}
                                                                            </span>
                                                                            <div className="flex items-center gap-1.5 mt-0.5">
                                                                                <span className="text-[10px] font-bold text-slate-400">
                                                                                    Peso: {param.weight}%
                                                                                </span>
                                                                                <span className={`text-[9px] font-black uppercase px-1.5 py-0.2 rounded ${
                                                                                    param.indicatorType === 'Automatico'
                                                                                        ? 'bg-purple-100 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300'
                                                                                        : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400'
                                                                                }`}>
                                                                                    {param.indicatorType === 'Automatico' ? `Auto (${param.automaticCode})` : 'Manual'}
                                                                                </span>
                                                                            </div>
                                                                        </div>

                                                                        {/* Info Icon Tooltip Trigger */}
                                                                        <button
                                                                            type="button"
                                                                            onClick={() => setInfoModalParam(param)}
                                                                            className="p-1 rounded-md text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-950/30 transition-colors cursor-pointer shrink-0"
                                                                            title="Ver detalle del indicador"
                                                                        >
                                                                            <span className="material-symbols-outlined text-[17px]">info</span>
                                                                        </button>
                                                                    </div>
                                                                </td>

                                                                {/* Cols 3..N: Subcontractor Score Cells */}
                                                                {activeSubcontractors
                                                                    .filter(s => s.razonSocial.toLowerCase().includes(subSearch.toLowerCase()) || s.rut.toLowerCase().includes(subSearch.toLowerCase()))
                                                                    .map(sub => {
                                                                        const cellScore = getCellScore(selectedPlanId!, sub.id, param, currentPlan!);

                                                                        return (
                                                                            <td 
                                                                                key={sub.id} 
                                                                                className="px-3 py-2 text-center border-r border-slate-200 dark:border-slate-800"
                                                                            >
                                                                                <div className="flex items-center justify-center gap-2">
                                                                                    {param.indicatorType === 'Manual' ? (
                                                                                        <input
                                                                                            type="number"
                                                                                            min="0"
                                                                                            max="100"
                                                                                            value={cellScore ?? 100}
                                                                                            onChange={e => handleScoreChange(selectedPlanId!, sub.id, param.id, parseFloat(e.target.value))}
                                                                                            disabled={!hasEditPermission}
                                                                                            className="w-16 px-2 py-1 bg-slate-50 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg text-center font-bold text-xs text-slate-800 dark:text-white outline-none focus:ring-2 focus:ring-blue-500"
                                                                                        />
                                                                                    ) : cellScore !== null ? (
                                                                                        <div 
                                                                                            className="flex items-center gap-1 bg-purple-50/70 dark:bg-purple-950/40 border border-purple-200 dark:border-purple-900/50 px-2 py-1 rounded-lg select-none"
                                                                                            title="Cálculo automático promediado de usuarios asignados en el periodo"
                                                                                        >
                                                                                            <span className="material-symbols-outlined text-[13px] text-purple-600 dark:text-purple-400">auto_awesome</span>
                                                                                            <span className="font-extrabold text-xs text-purple-900 dark:text-purple-200">{cellScore}%</span>
                                                                                        </div>
                                                                                    ) : (
                                                                                        <div 
                                                                                            className="flex items-center gap-1 bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 px-2 py-1 rounded-lg text-slate-400 select-none"
                                                                                            title="Sin compromisos ni usuarios activos registrados en el periodo"
                                                                                        >
                                                                                            <span className="material-symbols-outlined text-[13px] text-slate-400">auto_awesome</span>
                                                                                            <span className="font-bold text-xs text-slate-500 dark:text-slate-400">N/A</span>
                                                                                        </div>
                                                                                    )}

                                                                                    {/* Visual Badge corresponding to the range */}
                                                                                    {cellScore !== null && (
                                                                                        <EvaluationRangeBadge
                                                                                            score={cellScore}
                                                                                            ranges={ranges}
                                                                                            showDistinction={false}
                                                                                            showScore={false}
                                                                                        />
                                                                                    )}
                                                                                </div>
                                                                            </td>
                                                                        );
                                                                    })}
                                                            </tr>
                                                        ))}
                                                    </React.Fragment>
                                                ))}

                                                {/* ========================================================= */}
                                                {/* FINAL ROW: PROMEDIO / NOTA FINAL SUBCONTRATO              */}
                                                {/* ========================================================= */}
                                                <tr className="bg-slate-100 dark:bg-slate-800 border-t-2 border-slate-300 dark:border-slate-600 sticky bottom-0 z-20 shadow-md">
                                                    <td 
                                                        colSpan={2}
                                                        className="px-6 py-4 font-black text-sm uppercase tracking-wider text-slate-900 dark:text-white sticky left-0 z-30 bg-slate-100 dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <span className="material-symbols-outlined text-emerald-600 text-[20px]">workspace_premium</span>
                                                            <span>Promedio / Nota Final Subcontrato</span>
                                                        </div>
                                                    </td>

                                                    {activeSubcontractors
                                                        .filter(s => s.razonSocial.toLowerCase().includes(subSearch.toLowerCase()) || s.rut.toLowerCase().includes(subSearch.toLowerCase()))
                                                        .map(sub => {
                                                            const finalScore = calculateSubcontractorTotalScore(selectedPlanId!, sub.id, currentPlan!);

                                                            return (
                                                                <td 
                                                                    key={sub.id} 
                                                                    className="px-4 py-3 text-center border-r border-slate-200 dark:border-slate-700"
                                                                >
                                                                    <div className="flex flex-col items-center gap-1">
                                                                        <span className="text-base font-black text-slate-900 dark:text-white">
                                                                            {finalScore !== null ? `${finalScore}%` : 'N/A'}
                                                                        </span>
                                                                        {finalScore !== null ? (
                                                                            <EvaluationRangeBadge
                                                                                score={finalScore}
                                                                                ranges={ranges}
                                                                                showDistinction={true}
                                                                                showScore={false}
                                                                            />
                                                                        ) : (
                                                                            <span className="text-[10px] font-bold text-slate-400 italic">
                                                                                Sin datos
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            );
                                                        })}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="p-4 bg-slate-50 dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 flex items-center justify-between shrink-0">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-xl transition-colors cursor-pointer"
                    >
                        Cerrar
                    </button>

                    {selectedPlanId !== null && (
                        <span className="text-xs text-slate-400">
                            Semana #{selectedPlanId} • {activeSubcontractors.length} Subcontratistas
                        </span>
                    )}
                </div>
            </div>

            {/* Modal de Información del Indicador */}
            {infoModalParam && (
                <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-150">
                    <div className="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl border border-slate-200 dark:border-slate-800 w-full max-w-md p-5 flex flex-col gap-4">
                        <div className="flex items-center justify-between border-b border-slate-200 dark:border-slate-800 pb-3">
                            <h4 className="text-sm font-extrabold text-slate-800 dark:text-white uppercase flex items-center gap-2">
                                <span className="material-symbols-outlined text-blue-600">info</span>
                                Información del Indicador
                            </h4>
                            <button onClick={() => setInfoModalParam(null)} className="text-slate-400 hover:text-slate-600 cursor-pointer">
                                <span className="material-symbols-outlined text-[18px]">close</span>
                            </button>
                        </div>

                        <div className="space-y-3 text-xs">
                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Parámetro</span>
                                <p className="text-sm font-bold text-slate-800 dark:text-white mt-0.5">{infoModalParam.name}</p>
                            </div>

                            <div className="grid grid-cols-2 gap-2 bg-slate-50 dark:bg-slate-800 p-2.5 rounded-xl">
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Tipo</span>
                                    <span className="font-bold text-slate-700 dark:text-slate-200">{infoModalParam.indicatorType}</span>
                                </div>
                                <div>
                                    <span className="text-[10px] font-bold text-slate-400 uppercase block">Ponderación</span>
                                    <span className="font-bold text-blue-600 dark:text-blue-400">{infoModalParam.weight}%</span>
                                </div>
                            </div>

                            <div>
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">Criterio / Descripción del Indicador</span>
                                <div className="mt-1 p-3 bg-blue-50/60 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/40 rounded-xl text-slate-700 dark:text-slate-300 leading-relaxed">
                                    {infoModalParam.indicatorDescription || 'No se ingresó una descripción detallada para este indicador en la configuración.'}
                                </div>
                            </div>
                        </div>

                        <div className="flex justify-end pt-2 border-t border-slate-200 dark:border-slate-800">
                            <button
                                onClick={() => setInfoModalParam(null)}
                                className="px-5 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold rounded-xl shadow cursor-pointer"
                            >
                                Entendido
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
